import React, { FC, useEffect } from 'react';
import { GridCol, GridRow } from '@naf/grid';
import { StepWizardChildProps } from 'react-step-wizard';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useStateMachine } from 'little-state-machine';
import useSelector from '@redux/typedHooks';
import { WizardNavigation } from '@pages/BecomeAMember/components/WizardNavigation';
import { Consents as ConsentFields } from '@pages/BecomeAMember/forms/Consents/Consents';
import { MainMemberConsent } from '@appTypes/membersFormType';
import { Summary } from '../../components/Summary/Summary';
import { updateAction } from '../../lib/updateAction';
import { ConsentsSchema } from '../../schemas/ConsentsSchema';
import { useTrackingStep4 } from '../../utils/useIDPTracking';
import * as S from './Consents.styles';

type IdpConsentsFormType = {
  consents: MainMemberConsent[];
};

export const IdpConsents: FC<Partial<StepWizardChildProps>> = ({
  totalSteps,
  currentStep,
  nextStep,
  previousStep,
  goToStep,
}) => {
  const serverErrors = useSelector((state) => state.order.validation?.validationResults);
  const {
    actions,
    state: { internationalDrivingPermitForm, idpCustomerForm },
  } = useStateMachine({ updateAction });

  const {
    register,
    watch,
    control,
    handleSubmit,
    unregister,
    setError,
    setFocus,
    clearErrors,
    setValue,
    trigger,
    formState,
    formState: { errors },
    reset,
    resetField,
    getValues,
    getFieldState,
  } = useForm<IdpConsentsFormType>({
    resolver: yupResolver(ConsentsSchema),
    mode: 'onChange',
  });

  useEffect(() => {
    if (!internationalDrivingPermitForm?.membership?.productId && goToStep) {
      goToStep(3);
    }
  }, [goToStep, internationalDrivingPermitForm?.membership?.productId]);

  useEffect(() => {
    const consentError = serverErrors?.find((error) => error.field.startsWith('CustomerProductsRequests[0].Consents'));
    if (consentError) {
      setError('consents.0.undetermined', { type: 'server', message: consentError.message });
    }
  }, [serverErrors, setError]);

  const onSubmit = (data: IdpConsentsFormType) => {
    actions.updateAction({
      formName: 'idpCustomerForm',
      data: {
        ...idpCustomerForm,
        ...data,
      },
    });
  };

  const onGoBack = (data: IdpConsentsFormType) => {
    actions.updateAction({
      formName: 'idpCustomerForm',
      data: {
        ...idpCustomerForm,
        ...data,
      },
    });
  };

  const selectedConsents = watch('consents');

  useTrackingStep4(internationalDrivingPermitForm?.product?.productId);

  return (
    <FormProvider
      register={register}
      watch={watch}
      control={control}
      handleSubmit={handleSubmit}
      unregister={unregister}
      setError={setError}
      setFocus={setFocus}
      clearErrors={clearErrors}
      setValue={setValue}
      trigger={trigger}
      formState={formState}
      getValues={getValues}
      reset={reset}
      resetField={resetField}
      getFieldState={getFieldState}
    >
      <GridRow>
        <S.StyledGridCol s="12" m="12" l="8" xl="8">
          <form onSubmit={handleSubmit(onSubmit)}>
            <ConsentFields
              setValue={setValue}
              control={control}
              clearErrors={clearErrors}
              errors={errors}
              consentPath="consents"
              initialConsents={idpCustomerForm?.consents}
            />
          </form>
        </S.StyledGridCol>
        <GridCol s="12" m="12" l="4" xl="4" justify={{ s: 'stretch', m: 'stretch', l: 'end', xl: 'end' }}>
          <Summary products={internationalDrivingPermitForm?.product} />
        </GridCol>
        <GridCol s="12" m="12" l="12" xl="12">
          <WizardNavigation
            onGoback={onGoBack}
            totalSteps={totalSteps}
            currentStep={currentStep}
            nextStep={nextStep}
            previousStep={previousStep}
            isValid={
              Array.isArray(selectedConsents) &&
              selectedConsents.length > 0 &&
              selectedConsents.every((consent) => consent?.undetermined === false)
            }
            onSubmit={onSubmit}
            getValues={getValues}
          />
        </GridCol>
      </GridRow>
    </FormProvider>
  );
};
